import { openAiTesterSession } from "./openaiLiveTester";
import { getAuthSnapshot } from "./auth-snapshot";
/** Default-off storefront bridge. No SDK, cookie, identifier capture or request while off. */
import { hasAdsConsent, readConsent, CONSENT_EVENT } from "./cookie-consent";
export const OPENAI_BRIDGE_CLIENT_ENABLED = false as boolean;
/** Explicit source rollout selector; remains pinned until independently reviewed. */
export const OPENAI_BRIDGE_CLIENT_MODE = "test" as "test" | "production";
const browserScopeAllowed = () => OPENAI_BRIDGE_CLIENT_MODE === "production" || !!openAiTesterSession();
export const OPENAI_LINK_ATTRIBUTE = "_lecmo_oai_link";
const ENDPOINT = "/api/public/openai-consent";
const VERSION = "lecmo-ads-consent-v1";
export interface CartAttribute {
  key: string;
  value: string;
}
export function mergeOpenAiLinkAttribute(
  attrs: readonly CartAttribute[],
  link: string | null,
): CartAttribute[] {
  const rest = attrs.filter((a) => a.key !== OPENAI_LINK_ATTRIBUTE);
  return link ? [...rest, { key: OPENAI_LINK_ATTRIBUTE, value: link }] : rest;
}
function readCsrf(): string | null {
  return (
    document.cookie.match(/(?:^|;\s*)__Host-lecmo_oai_csrf=([A-Za-z0-9_-]{43})(?:;|$)/)?.[1] ?? null
  );
}
/** Injected seams are for offline tests; production passes the hard-off flag. */
export function createOpenAiBridgeClient(d: {
  enabled: boolean;
  allowed: () => boolean;
  csrf: () => string | null;
  fetch: typeof fetch;
  now: () => number;
  oppref: () => string | null;
  testerSession?: () => string | null;
  identity?: () => { userId: string | null; identityRevision: number };
  readPending?: () => boolean;
  writePending?: (pending: boolean) => void;
}) {
  let seq = 0;
  let generation = 0;
  let pendingWithdrawal = d.enabled ? (d.readPending?.() ?? false) : false;
  const setPending = (pending: boolean) => {
    pendingWithdrawal = pending;
    d.writePending?.(pending);
  };
  let tail: Promise<unknown> = Promise.resolve();
  const nextSeq = () => (seq = Math.max(d.now(), seq + 1));
  const post = async (body: unknown, csrf?: string) => {
    const tester = d.testerSession?.();
    return (
    d.fetch(ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      signal: AbortSignal.timeout(4000),
      headers: {
        "Content-Type": "application/json",
        ...(tester ? { "x-lecmo-oai-test-session": tester } : {}),
        ...(csrf ? { "x-lecmo-oai-csrf": csrf } : {}),
      },
      body: JSON.stringify(body),
    }));
  };
  const serial = <T>(fn: () => Promise<T>): Promise<T> => {
    const task = tail.then(fn, fn);
    tail = task.catch(() => {});
    return task;
  };
  const withdrawNow = async () => {
    const csrf = d.csrf();
    if (!csrf) return false; // keep pending if authority is inaccessible
    const ok = (await post({ action: "withdraw", seq: nextSeq() }, csrf)).status === 204;
    if (ok) setPending(false);
    return ok;
  };
  return {
    withdraw(): Promise<void> {
      if (!d.enabled || (d.testerSession && !d.testerSession())) return Promise.resolve();
      generation++;
      setPending(true);
      return serial(async () => {
        try {
          await withdrawNow();
        } catch {
          /* retry at next handoff */
        }
      });
    },
    link(): Promise<string | null> {
      if (!d.enabled || (d.testerSession && !d.testerSession())) return Promise.resolve(null);
      const observed = generation;
      const initiating = d.identity?.();
      const sameIdentity = () => {
        const current = d.identity?.();
        return !initiating || (current?.userId === initiating.userId && current?.identityRevision === initiating.identityRevision);
      };
      return serial(async () => {
        try {
          if (pendingWithdrawal && !(await withdrawNow())) return null;
          if (!d.allowed() || observed !== generation || !sameIdentity()) return null;
          let csrf = d.csrf();
          if (!csrf) {
            if ((await post({ action: "session", marketingAllowed: true })).status !== 204)
              return null;
            csrf = d.csrf();
          }
          if (!csrf || !d.allowed() || observed !== generation || !sameIdentity()) return null;
          const response = await post(
            {
              action: "grant",
              marketingAllowed: true,
              consentVersion: VERSION,
              oppref: d.oppref(),
              seq: nextSeq(),
            },
            csrf,
          );
          if (response.status !== 200) return null;
          const body = (await response.json()) as { link?: unknown };
          if (!d.allowed() || observed !== generation || !sameIdentity()) {
            setPending(true);
            await withdrawNow();
            return null;
          }
          return typeof body.link === "string" && /^[A-Za-z0-9_.-]{1,200}$/.test(body.link)
            ? body.link
            : null;
        } catch {
          return null;
        }
      });
    },
  };
}
let capturedOppref: string | null = null;
function captureConsentedClick() {
  if (!OPENAI_BRIDGE_CLIENT_ENABLED || !browserScopeAllowed() || !hasAdsConsent()) {
    capturedOppref = null;
    return;
  }
  const values = new URL(window.location.href).searchParams.getAll("oppref");
  if (values.length === 1 && /^[\x21-\x7e]{1,512}$/.test(values[0])) capturedOppref = values[0];
}
const client = createOpenAiBridgeClient({
  enabled: OPENAI_BRIDGE_CLIENT_ENABLED,
  testerSession: OPENAI_BRIDGE_CLIENT_MODE === "test" ? openAiTesterSession : undefined,
  identity: getAuthSnapshot,
  allowed: hasAdsConsent,
  csrf: readCsrf,
  fetch: (...args) => fetch(...args),
  now: Date.now,
  oppref: () => capturedOppref,
  readPending: () => {
    try {
      return localStorage.getItem("lecmo_oai_withdraw_pending") === "1";
    } catch {
      return true; // inaccessible withdrawal history cannot authorize a new grant
    }
  },
  writePending: (pending) => {
    try {
      if (pending) localStorage.setItem("lecmo_oai_withdraw_pending", "1");
      else localStorage.removeItem("lecmo_oai_withdraw_pending");
    } catch {
      /* Browser may block persistence; server still checks every delivery. */
    }
  },
});
/** Tester-only first-party rehearsal; initial denial has no server session to revoke. */
export async function syncOpenAiBridgeChoice(
  bridge: Pick<ReturnType<typeof createOpenAiBridgeClient>, "link" | "withdraw">,
  allowed: boolean,
  hasSessionAuthority: boolean,
): Promise<void> {
  if (allowed) await bridge.link();
  else if (hasSessionAuthority) await bridge.withdraw();
}
/** Mounted once; the hard flag and tester token guard all consent collection. */
export function startOpenAiConsentBridge(): () => void {
  if (!OPENAI_BRIDGE_CLIENT_ENABLED || !browserScopeAllowed() || typeof window === "undefined")
    return () => {};
  const changed = () => {
    captureConsentedClick();
    void syncOpenAiBridgeChoice(client, hasAdsConsent(), !!readCsrf());
  };
  changed();
  window.addEventListener(CONSENT_EVENT, changed);
  window.addEventListener("storage", changed);
  return () => {
    window.removeEventListener(CONSENT_EVENT, changed);
    window.removeEventListener("storage", changed);
  };
}
export async function prepareOpenAiCartAttributes(
  attrs: readonly CartAttribute[],
): Promise<CartAttribute[]> {
  if (!OPENAI_BRIDGE_CLIENT_ENABLED || !browserScopeAllowed()) return [...attrs];
  captureConsentedClick();
  if (readConsent()?.ads !== true) await client.withdraw();
  const link = await client.link();
  const merged = mergeOpenAiLinkAttribute(attrs, link);
  // Shopify attribute omission does not reliably clear a previously stored pointer.
  if (!link && attrs.some((a) => a.key === OPENAI_LINK_ATTRIBUTE))
    merged.push({ key: OPENAI_LINK_ATTRIBUTE, value: "" });
  return merged;
}
/** Kept for existing consumers; hard-off and current consent always take precedence. */
export async function requestOpenAiCartLink(_input: {
  marketingAllowed: boolean;
  oppref: string | null;
  consentVersion: string;
  seq: number;
  fetchImpl?: typeof fetch;
}): Promise<string | null> {
  if (!OPENAI_BRIDGE_CLIENT_ENABLED || !browserScopeAllowed() || !_input.marketingAllowed)
    return null;
  return client.link();
}
